import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { 
  Popover, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  Typography,
  CircularProgress,
  Box,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useGetFilteredAssetRequestsQuery } from '../../features/assetrequest/assetReqApiSlice';
import { useGetFilteredConsumableRequestsQuery } from '../../features/consumablerequest/consumableReqApiSlice';
import { useGetComplaintsQuery } from '../../features/complaints/complaintsApiSlice';
import useAuth from '../../features/hooks/useAuth';
import { assetInboxTags } from '../Assets/Myrequests/Inbox/assetInboxTags';
import { consumInboxTags } from '../Consumables/ConsumablesReq/Inbox/consumInboxTags';

const DropdownContainer = styled.div`
  max-height: 500px;
  min-width: 400px;
  max-width: 450px;
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #a961f1, #57ccf4);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #9851e1, #47bce4);
  }
`;

const NotificationItem = styled(ListItem)`
  &:hover {
    background-color: rgba(169, 97, 241, 0.08) !important;
    cursor: pointer;
  }
  
  border-left: 4px solid ${props => {
    switch(props.type) {
      case 'asset': return '#4caf50';
      case 'consumable': return '#ff9800';
      case 'complaint': return '#f44336';
      default: return '#a961f1';
    }
  }};
  
  padding: 12px 16px !important;
  transition: background-color 0.2s ease;
`;

const NotificationHeader = styled(Box)`
  padding: 16px;
  background: linear-gradient(135deg, #a961f1, #57ccf4);
  color: white;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NotificationTitle = styled(Typography)`
  font-weight: 600 !important;
  font-size: 14px !important;
  color: #333;
  margin-bottom: 4px !important;
`;

const NotificationSubtext = styled(Typography)`
  font-size: 12px !important;
  color: #666;
  margin-bottom: 2px !important;
`;

const TypeChip = styled(Chip)`
  height: 20px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  margin-top: 4px !important;
`;

const EmptyState = styled(Box)`
  padding: 40px 20px;
  text-align: center;
  color: #999;
`;

const LoadingState = styled(Box)`
  padding: 40px 20px;
  text-align: center;
`;

const NotificationDropdown = ({ anchorEl, open, onClose }) => {
  const navigate = useNavigate();
  const Auth = useAuth();
  const [isNavigating, setIsNavigating] = useState(false);

  // Get all tags that this user should see in their inbox
  const userInboxTags = useMemo(() => {
    const allTags = [];
    
    // Combine asset and consumable inbox tags based on user's roles
    Object.values(assetInboxTags).forEach(tagArray => {
      if (Array.isArray(tagArray)) {
        allTags.push(...tagArray);
      }
    });
    
    Object.values(consumInboxTags).forEach(tagArray => {
      if (Array.isArray(tagArray)) {
        allTags.push(...tagArray);
      }
    });
    
    return allTags;
  }, []);

  // Fetch unread asset requests (limited to 10) - filter by perm_curr_sent_to
  const { data: assetData, isLoading: assetLoading } = useGetFilteredAssetRequestsQuery({
    page: 1,
    limit: 10,
    filters: {
      isRead: false,
      perm_curr_sent_to: [Auth?.id],  // Requests sent to this user
      perm_curr_sent_to_tag: Object.values(assetInboxTags).flat()  // All possible inbox tags
    }
  }, {
    skip: !Auth?.id || !open,
    refetchOnMountOrArgChange: true
  });

  // Fetch unread consumable requests (limited to 10) - filter by perm_curr_sent_to
  const { data: consumableData, isLoading: consumableLoading } = useGetFilteredConsumableRequestsQuery({
    page: 1,
    limit: 10,
    filters: {
      isRead: false,
      perm_curr_sent_to: [Auth?.id],  // Requests sent to this user
      perm_curr_sent_to_tag: Object.values(consumInboxTags).flat()  // All possible inbox tags
    }
  }, {
    skip: !Auth?.id || !open,
    refetchOnMountOrArgChange: true
  });

  // Fetch unread complaints (limited to 10)
  const { complaints, isLoading: complaintsLoading } = useGetComplaintsQuery("complaints", {
    selectFromResult: ({ data, isLoading }) => ({
      complaints: data?.ids
        ?.map(id => data?.entities[id])
        ?.filter(complaint => {
          // Filter complaints for the logged-in user
          if (!complaint || complaint.isRead) return false;
          
          // Check if the complaint is assigned to or involves this user
          return (
            complaint.userId === Auth?.id ||
            complaint.assignedTo === Auth?.id ||
            complaint.supportUserId === Auth?.id
          );
        })
        ?.slice(0, 10) || [],
      isLoading
    }),
    skip: !Auth?.id || !open,
    refetchOnMountOrArgChange: true
  });

  // Combine all notifications and sort by date
  const allNotifications = useMemo(() => {
    const notifications = [];

    // Add asset requests
    if (assetData?.requests) {
      assetData.requests.forEach(request => {
        // Get the last object from req_data array for sender info and timestamp
        const lastReqData = request.req_data && request.req_data.length > 0 
          ? request.req_data[request.req_data.length - 1] 
          : null;
        
        // Extract name from sent_by object (it's an object with {id, name, office, designation})
        // name itself is an object with {firstName, middleName, lastName}
        let fromUserName = 'Unknown';
        if (lastReqData?.sent_by) {
          if (typeof lastReqData.sent_by === 'string') {
            fromUserName = lastReqData.sent_by;
          } else if (lastReqData.sent_by.name) {
            const nameObj = lastReqData.sent_by.name;
            if (typeof nameObj === 'string') {
              fromUserName = nameObj;
            } else if (nameObj.firstName || nameObj.middleName || nameObj.lastName) {
              fromUserName = [
                nameObj.firstName,
                nameObj.middleName,
                nameObj.lastName
              ].filter(Boolean).join(' ');
            }
          }
        } else if (request.user_name) {
          fromUserName = request.user_name;
        } else if (request.userName) {
          fromUserName = request.userName;
        }
        
        notifications.push({
          id: request.id,
          type: 'asset',
          reqId: request.uniqueReqId || request.orderNumber || request.id.slice(-8),
          reqType: request.req_type || 'Asset Request',
          fromUser: fromUserName,
          receiptTimestamp: lastReqData?.time_stamp 
            ? new Date(lastReqData.time_stamp) 
            : new Date(request.createdAt || request.timestamp),
          status: request.curr_status,
          data: request
        });
      });
    }

    // Add consumable requests
    if (consumableData?.requests) {
      consumableData.requests.forEach(request => {
        // Get the last object from req_data array for sender info and timestamp
        const lastReqData = request.req_data && request.req_data.length > 0 
          ? request.req_data[request.req_data.length - 1] 
          : null;
        
        // Extract name from sent_by object (it's an object with {id, name, office, designation})
        // name itself is an object with {firstName, middleName, lastName}
        let fromUserName = 'Unknown';
        if (lastReqData?.sent_by) {
          if (typeof lastReqData.sent_by === 'string') {
            fromUserName = lastReqData.sent_by;
          } else if (lastReqData.sent_by.name) {
            const nameObj = lastReqData.sent_by.name;
            if (typeof nameObj === 'string') {
              fromUserName = nameObj;
            } else if (nameObj.firstName || nameObj.middleName || nameObj.lastName) {
              fromUserName = [
                nameObj.firstName,
                nameObj.middleName,
                nameObj.lastName
              ].filter(Boolean).join(' ');
            }
          }
        } else if (request.user_name) {
          fromUserName = request.user_name;
        } else if (request.userName) {
          fromUserName = request.userName;
        }
        
        notifications.push({
          id: request.id,
          type: 'consumable',
          reqId: request.uniqueReqId || request.orderNumber || request.id.slice(-8),
          reqType: request.req_type || 'Consumable Request',
          fromUser: fromUserName,
          receiptTimestamp: lastReqData?.time_stamp 
            ? new Date(lastReqData.time_stamp) 
            : new Date(request.createdAt || request.timestamp),
          status: request.curr_status,
          data: request
        });
      });
    }

    // Add complaints
    if (complaints) {
      complaints.forEach(complaint => {
        notifications.push({
          id: complaint.id,
          type: 'complaint',
          reqId: complaint.id.slice(-8),
          reqType: complaint.complaintType || 'Complaint',
          fromUser: complaint.user_name || complaint.userName || 'Unknown',
          receiptTimestamp: new Date(complaint.createdAt || complaint.timestamp),
          status: complaint.status,
          data: complaint
        });
      });
    }

    // Sort by receipt timestamp (newest first) and limit to 15 total
    return notifications
      .sort((a, b) => b.receiptTimestamp - a.receiptTimestamp)
      .slice(0, 15);
  }, [assetData, consumableData, complaints]);

  const isLoading = assetLoading || consumableLoading || complaintsLoading;

  const handleNotificationClick = (notification) => {
    setIsNavigating(true);
    onClose();
    
    // Navigate to unified inbox with appropriate filters
    // The unified inbox will handle displaying the correct tab
    setTimeout(() => {
      navigate('/management/unifiedinbox', { 
        state: { 
          notificationId: notification.id,
          notificationType: notification.type,
          curr_sent_to_tag: notification.data?.curr_sent_to_tag
        } 
      });
      setIsNavigating(false);
    }, 100);
  };

  const formatDate = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    // Format the full date and time
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    const dateStr = date.toLocaleDateString('en-US', { 
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    // Return relative time with full timestamp
    if (minutes < 1) return `Just now (${timeStr})`;
    if (minutes < 60) return `${minutes}m ago (${timeStr})`;
    if (hours < 24) return `${hours}h ago (${timeStr})`;
    if (days < 7) return `${days}d ago (${dateStr} ${timeStr})`;
    return `${dateStr} ${timeStr}`;
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'asset': return 'success';
      case 'consumable': return 'warning';
      case 'complaint': return 'error';
      default: return 'primary';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'asset': return 'Asset';
      case 'consumable': return 'Consumable';
      case 'complaint': return 'Complaint';
      default: return 'Notification';
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        mt: 1,
        '& .MuiPopover-paper': {
          boxShadow: '0px 11px 15px -7px rgba(169,97,241,0.3), 0px 24px 38px 3px rgba(87,204,244,0.2)',
          borderRadius: '8px',
          overflow: 'hidden'
        }
      }}
    >
      <DropdownContainer>
        <NotificationHeader>
          <span>Notifications</span>
          <span style={{ fontSize: '14px', opacity: 0.9 }}>
            {allNotifications.length} unread
          </span>
        </NotificationHeader>

        {isLoading ? (
          <LoadingState>
            <CircularProgress size={40} sx={{ color: '#a961f1' }} />
            <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
              Loading notifications...
            </Typography>
          </LoadingState>
        ) : allNotifications.length === 0 ? (
          <EmptyState>
            <Typography variant="body1" sx={{ mb: 1 }}>
              🎉 No unread notifications
            </Typography>
            <Typography variant="body2">
              You're all caught up!
            </Typography>
          </EmptyState>
        ) : (
          <List sx={{ p: 0 }}>
            {allNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem
                  type={notification.type}
                  onClick={() => handleNotificationClick(notification)}
                  button
                >
                  <ListItemText
                    primary={
                      <Box>
                        <NotificationTitle>
                          Req ID: {notification.reqId}
                        </NotificationTitle>
                        <NotificationSubtext>
                          <strong>Type:</strong> {notification.reqType}
                        </NotificationSubtext>
                        <NotificationSubtext>
                          <strong>From:</strong> {notification.fromUser}
                        </NotificationSubtext>
                        <NotificationSubtext>
                          <strong>Received:</strong> {formatDate(notification.receiptTimestamp)}
                        </NotificationSubtext>
                        <TypeChip 
                          label={getTypeLabel(notification.type)} 
                          color={getTypeColor(notification.type)}
                          size="small"
                        />
                      </Box>
                    }
                  />
                </NotificationItem>
                {index < allNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DropdownContainer>
    </Popover>
  );
};

export default NotificationDropdown;

